// Pill.jsx

import React from 'react';

// Context
import { useIcons } from 'context/IconsContext';
import { useTheme } from 'context/ThemeContext';

// CSS
import './Pill.css';

// Icons
import Icon from 'svg/Icon';

/**
 * Pill
 *
 * Reusable small pill used for enum/status labels.
 *
 * @param {string} label - Text content.
 * @param {string} color - Optional background/border color.
 * @param {string} iconKey - Optional icon key shown before the label.
 * @param {string} iconSrc - Optional image URL shown before the label (e.g. source logos).
 * @param {Function} onClick - Optional click handler.
 * @param {Boolean} clickable - Whether pointer/hover state should be applied.
 * @param {Boolean} compact - Whether to render a smaller pill.
 * @param {string} className - Optional extra class names.
 * @param {string} title - Optional native tooltip.
 *
 * @returns {ReactNode} Pill component.
 */
const Pill = React.memo(({
  label,
  color = '',
  labelColor,
  iconKey = '',
  iconSrc = '',
  onClick,
  clickable = false,
  compact = false,
  className = '',
  title = '',
}) => {

    // ----- CONTEXT ----- //

    const { iconsByKey } = useIcons();

    // Theme Context
    const { theme } = useTheme();

    if (!label) return null;

    const isClickable = Boolean(clickable || onClick);
    const resolvedLabelColor = labelColor || theme.foreground;
    const resolvedIconSrc = typeof iconSrc === 'string' ? iconSrc.trim() : '';
    const normalizedIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    const resolvedIconKey = !resolvedIconSrc && normalizedIconKey && iconsByKey && !iconsByKey[normalizedIconKey]
        ? ''
        : (!resolvedIconSrc ? normalizedIconKey : '');
    const iconSize = compact ? 10 : 12;

    // ----- RENDER ----- //

    return (
        <span
          className={`pill-container${compact ? ' is-compact' : ''}${isClickable ? ' is-clickable' : ''}${className ? ` ${className}` : ''}`}
          style={{
                backgroundColor: color || 'rgba(255, 255, 255, 0.12)',
                color: resolvedLabelColor,
            }}
          title={title || undefined}
          onClick={isClickable ? onClick : undefined}
        >
            {resolvedIconSrc ? (
                <img
                  className="pill-icon-logo"
                  src={resolvedIconSrc}
                  alt=""
                  draggable={false}
                  width={compact ? 12 : 14}
                  height={compact ? 12 : 14}
                />
            ) : resolvedIconKey ? (
                <Icon
                  iconKey={resolvedIconKey}
                  width={iconSize}
                  height={iconSize}
                  color={resolvedLabelColor}
                />
            ) : null}
            {label}
        </span>
    );
});

// Dev Tools
Pill.displayName = 'Pill';

export default Pill;
